import { FC, useMemo, useState } from 'react';

import LockIcon from '@/assets/svg/ui-elements/lock.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import { Badge, Button, Input, theme, Tooltip, Typography } from '@/shared/design-system';

import {
  RoleGroup,
  RoleGroupHeader,
  RoleItem,
  RoleItemLabel,
  RoleItemText,
  RoleScroller,
  SidePanel,
} from '../styled';
import { RoleSummary } from '../types';

const SECTIONS: { key: 'system' | 'custom'; label: string; isSystem: boolean }[] = [
  { key: 'system', label: 'Built-in', isSystem: true },
  { key: 'custom', label: 'Custom', isSystem: false },
];

/** Below this many roles the list fits without scrolling, so a search box is noise. */
const SEARCH_THRESHOLD = 7;

export const HELD_ROLE_BADGE = 'You';
export const HELD_ROLE_TOOLTIP =
  'You hold this role, so any change you make to it also changes what you can do.';
export const PENDING_ROLE_BADGE = 'Pending';
export const PENDING_ROLE_TOOLTIP = 'Changes to this role are waiting for approval.';
export const UNUSED_ROLE_BADGE = 'Unused';
export const UNUSED_ROLE_TOOLTIP = 'Nobody holds this role yet.';
export const NO_CUSTOM_ROLES_MESSAGE = 'No custom roles yet. Duplicate a built-in role to start.';
export const NO_ROLE_MATCHES_MESSAGE = 'No roles match that search';

export type RoleListProps = {
  roles: readonly RoleSummary[];
  selectedRoleId?: string;
  onSelect: (_roleId: string) => void;
  /** Whether the caller may create roles. */
  canManage: boolean;
  onCreate: () => void;
  /**
   * Tooltip shown on a system role's lock icon. Every system role gets one —
   * what it says (and whether the role is merely "built-in" vs. fully frozen)
   * is plane-specific, so the caller supplies it rather than this component
   * guessing from the role's name.
   */
  lockTooltip: (_role: RoleSummary) => string;
  isHeldByCaller?: (_role: RoleSummary) => boolean;
  /** The secondary line under the name: a seeded role's description, a custom role's size. */
  subtitle?: (_role: RoleSummary) => string | null;
  /** Requests about the role still waiting for a decision. */
  pendingCount?: (_role: RoleSummary) => number;
};

/**
 * The role-picking side panel shared by every role-administration board
 * (entity plane, Gamma plane — GP-3853 Task 11). Purely presentational: which
 * roles are "frozen" or "non-assignable" is a plane-specific business rule the
 * caller decides elsewhere (e.g. which action buttons render), this component
 * only needs to know what tooltip to show on the lock icon.
 */
export const RoleList: FC<RoleListProps> = ({
  roles,
  selectedRoleId,
  onSelect,
  canManage,
  onCreate,
  lockTooltip,
  isHeldByCaller,
  subtitle,
  pendingCount,
}) => {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  const visibleRoles = useMemo(
    () =>
      needle
        ? roles.filter(
            (role) =>
              role.name.toLowerCase().includes(needle) ||
              subtitle?.(role)?.toLowerCase().includes(needle),
          )
        : roles,
    [roles, needle, subtitle],
  );

  return (
    <SidePanel>
      {roles.length >= SEARCH_THRESHOLD && (
        <Input
          size="sm"
          value={search}
          placeholder="Search roles"
          aria-label="Search roles"
          startIcon={<SearchIcon />}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}

      <RoleScroller>
        {needle && !visibleRoles.length && (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {NO_ROLE_MATCHES_MESSAGE}
          </Typography>
        )}

        {SECTIONS.map((section) => {
          const sectionRoles = visibleRoles.filter((role) => role.isSystem === section.isSystem);
          const showEmptyCustom = section.key === 'custom' && !needle && !sectionRoles.length;
          if (!sectionRoles.length && !showEmptyCustom) return null;

          return (
            <RoleGroup key={section.key}>
              <RoleGroupHeader>
                <Typography variant="body4" color={theme.colors.text.tertiary}>
                  {section.label}
                </Typography>
                <Typography variant="body4" color={theme.colors.text.tertiary}>
                  {sectionRoles.length}
                </Typography>
              </RoleGroupHeader>

              {showEmptyCustom && (
                <Typography variant="body4" color={theme.colors.text.secondary}>
                  {NO_CUSTOM_ROLES_MESSAGE}
                </Typography>
              )}

              {sectionRoles.map((role) => {
                const description = subtitle?.(role);
                const pending = pendingCount?.(role) ?? 0;

                return (
                  <RoleItem
                    key={role.id}
                    type="button"
                    $active={role.id === selectedRoleId}
                    aria-current={role.id === selectedRoleId}
                    onClick={() => onSelect(role.id)}
                  >
                    <RoleItemText>
                      <RoleItemLabel>
                        {role.isLocked && (
                          <Tooltip title={lockTooltip(role)}>
                            <span>
                              <LockIcon aria-label="Default role" />
                            </span>
                          </Tooltip>
                        )}
                        <Typography
                          variant="body3"
                          fontWeight={500}
                          color={theme.colors.text.primary}
                          noWrap
                        >
                          {role.name}
                        </Typography>
                        {isHeldByCaller?.(role) && (
                          <Tooltip title={HELD_ROLE_TOOLTIP}>
                            <span>
                              <Badge
                                size="sm"
                                variant="subtle"
                                color="primary"
                                label={HELD_ROLE_BADGE}
                              />
                            </span>
                          </Tooltip>
                        )}
                        {pending > 0 && (
                          <Tooltip title={PENDING_ROLE_TOOLTIP}>
                            <span>
                              <Badge
                                size="sm"
                                variant="subtle"
                                color="yellow"
                                label={PENDING_ROLE_BADGE}
                              />
                            </span>
                          </Tooltip>
                        )}
                        {!role.isSystem && role.memberCount === 0 && (
                          <Tooltip title={UNUSED_ROLE_TOOLTIP}>
                            <span>
                              <Badge
                                size="sm"
                                variant="subtle"
                                color="default"
                                label={UNUSED_ROLE_BADGE}
                              />
                            </span>
                          </Tooltip>
                        )}
                      </RoleItemLabel>

                      {description && (
                        <Typography variant="body4" color={theme.colors.text.tertiary}>
                          {description}
                        </Typography>
                      )}
                    </RoleItemText>

                    <Typography
                      variant="body4"
                      color={theme.colors.text.tertiary}
                      aria-label={`${role.memberCount} members`}
                    >
                      {role.memberCount}
                    </Typography>
                  </RoleItem>
                );
              })}
            </RoleGroup>
          );
        })}
      </RoleScroller>

      {canManage && (
        <Button variant="outline" size="sm" fullWidth onClick={onCreate}>
          <PlusIcon aria-hidden />
          New Role
        </Button>
      )}
    </SidePanel>
  );
};
